import { useState, useCallback } from 'react';
import { User, EditingId, EditingPharmacy, OperatorInfo } from '../types';
import { changeUserId, updatePharmacy } from '../allUsersApi';
import { notify } from '../../../notifications';
import { apiErrorMessage } from '../../reports/edit/editApi';

export const useInlineEditing = (
  users: User[],
  refetchUsers: () => void,
  setUsers: React.Dispatch<React.SetStateAction<User[]>> | undefined,
  operator: OperatorInfo | null
) => {
  const [editingId, setEditingId] = useState<EditingId | null>(null);
  const [editingPharmacy, setEditingPharmacy] = useState<EditingPharmacy | null>(null);
  const [previousId, setPreviousId] = useState<string>('');
  const [previousPharmacyNum, setPreviousPharmacyNum] = useState<string>('');

  // Системный ID меняет только ранг 100 (AGENTS.md §1.1), сервер проверяет то же самое
  const canEditId = useCallback(() => Boolean(operator && operator.isSuperAdmin), [operator]);

  const canEditPharmacyNum = useCallback((user: User) => user.canEditPharmacy === true, []);

  const startEditingId = useCallback((user: User) => {
    if (!canEditId()) return;
    setEditingId({
      userId: user.id,
      newId: user.id.toString()
    });
    setPreviousId(user.id.toString());
  }, [canEditId]);

  const startEditingPharmacy = useCallback((user: User) => {
    if (!canEditPharmacyNum(user)) return;
    setEditingPharmacy({
      userId: user.id,
      newPharmacyNum: user.pharmacyNum.toString()
    });
    setPreviousPharmacyNum(user.pharmacyNum.toString());
  }, [canEditPharmacyNum]);

  const saveNewId = useCallback(async (userId: number, newId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    if (newId === previousId) {
      setEditingId(null);
      return;
    }

    try {
      notify.success(await changeUserId(userId, parseInt(newId)));
      refetchUsers();
      setEditingId(null);
    } catch (error) {
      notify.error(apiErrorMessage(error, 'Не удалось изменить ID пользователя'));
      setEditingId(prev => prev ? { ...prev, newId: previousId } : null);
    }
  }, [users, previousId, refetchUsers]);

  const saveNewPharmacyNum = useCallback(async (userId: number, newPharmacyNum: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    if (newPharmacyNum === previousPharmacyNum) {
      setEditingPharmacy(null);
      return;
    }

    // Оптимистично применяем новый номер аптеки сразу в памяти
    if (setUsers) {
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, pharmacyNum: newPharmacyNum } : u));
    }
    setEditingPharmacy(null);

    try {
      notify.success(await updatePharmacy(userId, { pharmacyNum: newPharmacyNum.trim() }));
      refetchUsers();
    } catch (error) {
      if (setUsers) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, pharmacyNum: previousPharmacyNum } : u));
      }
      notify.error(apiErrorMessage(error, 'Не удалось изменить номер аптеки'));
    }
  }, [users, previousPharmacyNum, refetchUsers, setUsers]);

  return {
    editingId,
    editingPharmacy,
    canEditId,
    canEditPharmacyNum,
    startEditingId,
    startEditingPharmacy,
    saveNewId,
    saveNewPharmacyNum,
    setEditingId,
    setEditingPharmacy
  };
};
